import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  Chip,
  TextField,
  Alert,
  CircularProgress,
  LinearProgress,
  Checkbox,
  ToggleButton,
  ToggleButtonGroup,
  Stack,
  Divider,
  MenuItem,
  Dialog,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
} from '@mui/material';
import { UploadFile, Visibility, PlayArrow, Send, Edit, Download } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import adminService from '../../services/adminService';
import ResponsiveTableContainer from './shared/ResponsiveTableContainer';
import DialogHeader from '../Common/DialogHeader';
import { useConfirm } from '../Common/ConfirmDialog';

const BULK_CHUNK = 50;
const PREVIEW_ROW_LIMIT = 300;
// Import-állapot lekérdezése: 5 mp, rejtett böngészőfülön nem kérdezünk (a backend kéréskeretét kíméli)
const POLL_MS = 5000;

const REASON_LABELS = {
  NO_EMAIL: 'Nincs email',
  INVALID_EMAIL: 'Érvénytelen email',
  NO_NAME: 'Nincs név',
  LOW_QUALITY: 'Alacsony quality',
  UNKNOWN_CATEGORY: 'Ismeretlen kategória',
  DUPLICATE_EMAIL_IN_FILE: 'Duplikált email',
  DUPLICATE_ID_IN_FILE: 'Duplikált azonosító',
  EMAIL_TAKEN: 'Email már foglalt',
  ALREADY_IMPORTED: 'Már importálva',
  OPTED_OUT: 'Elutasította a meghívót',
  INVITE_EXPIRED: 'Lejárt meghívó',
  UNIQUE_CONFLICT: 'Ütközés',
  ERROR: 'Hiba',
};

const STATUS_COLORS = { RUNNING: 'info', DONE: 'success', FAILED: 'error', PENDING: 'default' };

const csvEscape = (v) => {
  const s = v === null || v === undefined ? '' : String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

const downloadCsv = (rows, fileName) => {
  const columns = ['externalId', 'businessName', 'email', 'phone', 'city', 'category', 'quality', 'action', 'reason', 'error'];
  const csv = [columns.join(','), ...rows.map(r => columns.map(c => csvEscape(r[c])).join(','))].join('\n');
  const blob = new Blob([`﻿${csv}`], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  a.click();
  URL.revokeObjectURL(url);
};

const darkButtonSx = { bgcolor: '#1b331f', '&:hover': { bgcolor: '#2a5d32' } };

export default function AdminProviderImportPage() {
  const [confirm, confirmDialog] = useConfirm();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [minQuality, setMinQuality] = useState(4);
  const [previewResult, setPreviewResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [alert, setAlert] = useState(null);

  const [batches, setBatches] = useState([]);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [batchProviders, setBatchProviders] = useState([]);
  const [invitedFilter, setInvitedFilter] = useState('no');
  const [selectedIds, setSelectedIds] = useState([]);
  const [inviting, setInviting] = useState(false);
  const [inviteProgress, setInviteProgress] = useState(null);
  // Meghívó-sablon: '' = automatikus (az aktív sablonok közül véletlenszerűen), különben a kiválasztott sablon
  const [inviteTemplates, setInviteTemplates] = useState([]);
  const [templateId, setTemplateId] = useState('');
  const [invitePreview, setInvitePreview] = useState(null); // { subject, html, to, businessName }
  const [previewBusy, setPreviewBusy] = useState(false);
  // Napi meghívó-keret: mai kiküldött / összes engedett (null = még nem töltődött be)
  const [inviteQuota, setInviteQuota] = useState(null);

  const loadInviteQuota = useCallback(() => {
    adminService.getInviteQuota().then(setInviteQuota).catch(() => setInviteQuota(null));
  }, []);

  useEffect(() => {
    adminService.getInviteTemplates().then(setInviteTemplates).catch(() => setInviteTemplates([]));
    loadInviteQuota();
  }, [loadInviteQuota]);

  const loadBatches = useCallback(async () => {
    try {
      setBatches(await adminService.getProviderImports());
    } catch (err) {
      setAlert({ severity: 'error', message: 'Hiba az importok betöltésekor.' });
    }
  }, []);

  useEffect(() => { loadBatches(); }, [loadBatches]);

  const loadBatchProviders = useCallback(async () => {
    if (!selectedBatch) return;
    try {
      setBatchProviders(await adminService.getProviderImportProviders(selectedBatch.id, invitedFilter));
      setSelectedIds([]);
    } catch (err) {
      setAlert({ severity: 'error', message: 'Hiba a szolgáltatók betöltésekor.' });
    }
  }, [selectedBatch, invitedFilter]);

  useEffect(() => { loadBatchProviders(); }, [loadBatchProviders]);

  // Futó batch státuszának pollozása
  useEffect(() => {
    if (selectedBatch?.status !== 'RUNNING') return undefined;
    const timer = setInterval(async () => {
      if (document.hidden) return;
      try {
        const fresh = await adminService.getProviderImport(selectedBatch.id);
        setSelectedBatch(fresh);
        if (fresh.status !== 'RUNNING') {
          loadBatches();
          loadBatchProviders();
        }
      } catch (err) {
        // átmeneti hiba — következő körben újra
      }
    }, POLL_MS);
    return () => clearInterval(timer);
  }, [selectedBatch, loadBatches, loadBatchProviders]);

  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!/\.(json|csv)$/i.test(f.name)) {
      setAlert({ severity: 'error', message: 'Csak .json vagy .csv fájl választható.' });
      return;
    }
    setFile(f);
    setPreviewResult(null);
    setAlert(null);
  };

  const handlePreview = async () => {
    setBusy(true);
    setAlert(null);
    try {
      setPreviewResult(await adminService.previewProviderImport(file, minQuality));
    } catch (err) {
      setAlert({ severity: 'error', message: err?.message || 'Hiba az előnézet során.' });
    } finally {
      setBusy(false);
    }
  };

  const handleImport = async () => {
    const count = previewResult?.summary?.toCreate || 0;
    if (!(await confirm({ title: 'Import indítása', message: `${count} DRAFT szolgáltató jön létre (meghívó NEM megy ki). Indítod az importot?`, confirmLabel: 'Import' }))) return;
    setBusy(true);
    setAlert(null);
    try {
      const { batchId } = await adminService.startProviderImport(file, minQuality);
      setPreviewResult(null);
      setFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      await loadBatches();
      setSelectedBatch(await adminService.getProviderImport(batchId));
      setAlert({ severity: 'success', message: 'Import elindult — a folyamat lent követhető.' });
    } catch (err) {
      setAlert({ severity: 'error', message: err?.message || 'Hiba az import indításakor.' });
    } finally {
      setBusy(false);
    }
  };

  const handleSelectBatch = async (batch) => {
    try {
      setSelectedBatch(await adminService.getProviderImport(batch.id));
    } catch (err) {
      setAlert({ severity: 'error', message: 'Hiba az import betöltésekor.' });
    }
  };

  const inviteable = batchProviders.filter(p => p.status === 'DRAFT');
  const allSelected = inviteable.length > 0 && inviteable.every(p => selectedIds.includes(p.id));
  const toggleAll = () => setSelectedIds(allSelected ? [] : inviteable.map(p => p.id));
  const toggleOne = (id) => setSelectedIds(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]));

  const chosenTemplate = inviteTemplates.find(t => t.id === templateId);

  // Előnézet egy konkrét szolgáltató adataival (a linkek mintalinkek)
  const openInvitePreview = async (provider) => {
    if (!templateId) {
      setAlert({ severity: 'info', message: 'Az előnézethez válassz egy sablont (az „Automatikus” választásnál nincs egy konkrét levél).' });
      return;
    }
    setPreviewBusy(true);
    try {
      const result = await adminService.previewProviderInvite(templateId, provider.id);
      setInvitePreview({ ...result, businessName: provider.businessName });
    } catch (err) {
      setAlert({ severity: 'error', message: err?.message || 'Az előnézet nem készült el.' });
    } finally {
      setPreviewBusy(false);
    }
  };
  const firstSelectedProvider = batchProviders.find(p => selectedIds.includes(p.id));

  const sendInvites = async (ids) => {
    if (ids.length === 0) return;
    if (inviteQuota && inviteQuota.sentToday >= inviteQuota.limit) {
      setAlert({ severity: 'warning', message: `A mai napi meghívó-keret (${inviteQuota.limit}) már elfogyott — ${inviteQuota.sentToday} meghívó ment ki ma. Holnap folytathatod.` });
      return;
    }
    const templateText = chosenTemplate ? `a(z) „${chosenTemplate.name}” sablonnal` : 'automatikusan választott sablonnal';
    if (!(await confirm({ title: 'Meghívók küldése', message: `${ids.length} szolgáltató kap meghívó emailt ${templateText}. Biztosan kiküldöd?`, confirmLabel: 'Küldés' }))) return;
    setInviting(true);
    setAlert(null);
    let sent = 0;
    const failed = [];
    let dailyLimitReached = null;
    try {
      for (let i = 0; i < ids.length && !dailyLimitReached; i += BULK_CHUNK) {
        setInviteProgress({ done: i, total: ids.length });
        const result = await adminService.sendProviderInvitesBulk(ids.slice(i, i + BULK_CHUNK), templateId || null);
        sent += result.sent.length;
        failed.push(...result.failed.filter(f => f.message !== 'napi keret'));
        dailyLimitReached = result.dailyLimitReached || null;
      }
      const notSent = ids.length - sent - failed.length;
      setAlert({
        severity: failed.length || dailyLimitReached ? 'warning' : 'success',
        message: `${sent} meghívó elküldve.`
          + (dailyLimitReached ? ` ${dailyLimitReached} ${notSent} meghívó nem ment ki — holnap a "Még nem meghívott" szűrővel folytathatod.` : '')
          + (failed.length ? ` ${failed.length} sikertelen: ${failed.map(f => `#${f.id} (${f.message})`).join(', ')}` : ''),
      });
    } catch (err) {
      setAlert({ severity: 'error', message: `${sent} elküldve, majd hiba: ${err?.message || 'ismeretlen hiba'}` });
    } finally {
      setInviting(false);
      setInviteProgress(null);
      loadBatchProviders();
      loadInviteQuota();
    }
  };

  const reportRows = Array.isArray(selectedBatch?.report) ? selectedBatch.report : [];
  const problemRows = reportRows.filter(r => r.action !== 'CREATE');
  const imageWarnings = reportRows.filter(r => r.image && r.image !== 'OK').length;

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" mb={2}>
        Szolgáltatólista (pl. <code>invite_providers/…/providers.json</code> vagy <code>.csv</code>) importja: minden érvényes, emaillel
        rendelkező rekordból jelszó nélküli fiók és kitöltött <strong>DRAFT</strong> profil jön létre (kategória, cím, elérhetőségek,
        linkek, profilkép). A meghívókat utána, átnézés után külön küldheted ki. A profil csak akkor lesz nyilvános, ha a szolgáltató jóváhagyja.
      </Typography>

      {alert && (
        <Alert severity={alert.severity} onClose={() => setAlert(null)} sx={{ mb: 2 }}>
          {alert.message}
        </Alert>
      )}

      {/* 1. Fájl + előnézet */}
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
          <Button variant="outlined" component="label" startIcon={<UploadFile />}>
            {file ? file.name : 'Fájl kiválasztása'}
            <input ref={fileInputRef} hidden type="file" accept=".json,.csv" onChange={handleFileChange} />
          </Button>
          <TextField
            label="Minimum quality"
            type="number"
            size="small"
            value={minQuality}
            onChange={(e) => { setMinQuality(Number(e.target.value)); setPreviewResult(null); }}
            inputProps={{ min: 0, max: 6 }}
            sx={{ width: 150 }}
          />
          <Button variant="contained" startIcon={<Visibility />} disabled={!file || busy} onClick={handlePreview} sx={darkButtonSx}>
            Előnézet
          </Button>
          <Button
            variant="contained"
            color="warning"
            startIcon={<PlayArrow />}
            disabled={!previewResult || !previewResult.summary.toCreate || busy}
            onClick={handleImport}
          >
            Importálás
          </Button>
          {busy && <CircularProgress size={22} />}
        </Stack>

        {previewResult && (
          <Box mt={2}>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap mb={1}>
              <Chip label={`Összes: ${previewResult.summary.total}`} />
              <Chip color="success" label={`Létrejön: ${previewResult.summary.toCreate}`} />
              {Object.entries(previewResult.summary.skippedByReason).map(([reason, count]) => (
                <Chip key={reason} variant="outlined" color="warning" label={`${REASON_LABELS[reason] || reason}: ${count}`} />
              ))}
              {previewResult.summary.skipped > 0 && (
                <Button size="small" startIcon={<Download />}
                  onClick={() => downloadCsv(previewResult.rows.filter(r => r.action === 'SKIP'), 'kihagyott_szolgaltatok.csv')}>
                  Kihagyottak CSV
                </Button>
              )}
            </Stack>
            <ResponsiveTableContainer component="div" sx={{ maxHeight: 420 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Név</TableCell>
                    <TableCell>Kategória</TableCell>
                    <TableCell>Város</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Quality</TableCell>
                    <TableCell>Eredmény</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {previewResult.rows.slice(0, PREVIEW_ROW_LIMIT).map((r, idx) => (
                    <TableRow key={`${r.externalId}-${idx}`}>
                      <TableCell>{r.businessName}</TableCell>
                      <TableCell>{r.category}</TableCell>
                      <TableCell>{r.city}</TableCell>
                      <TableCell>{r.email || '—'}</TableCell>
                      <TableCell>{r.quality}</TableCell>
                      <TableCell>
                        {r.action === 'CREATE'
                          ? <Chip size="small" color="success" label="Létrejön" />
                          : <Chip size="small" variant="outlined" label={REASON_LABELS[r.reason] || r.reason} />}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </ResponsiveTableContainer>
            {previewResult.rows.length > PREVIEW_ROW_LIMIT && (
              <Typography variant="caption" color="text.secondary">
                Az első {PREVIEW_ROW_LIMIT} sor látszik a {previewResult.rows.length}-ból.
              </Typography>
            )}
          </Box>
        )}
      </Paper>

      {/* 2. Korábbi importok */}
      <Typography variant="subtitle1" fontWeight={600} mb={1}>Importok</Typography>
      <ResponsiveTableContainer sx={{ mb: 3 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>#</TableCell>
              <TableCell>Fájl</TableCell>
              <TableCell>Indítva</TableCell>
              <TableCell>Státusz</TableCell>
              <TableCell>Létrejött / Kihagyva / Hiba</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {batches.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 3, color: 'text.secondary' }}>Még nem volt import.</TableCell>
              </TableRow>
            ) : batches.map(b => (
              <TableRow key={b.id} hover selected={selectedBatch?.id === b.id} sx={{ cursor: 'pointer' }} onClick={() => handleSelectBatch(b)}>
                <TableCell>{b.id}</TableCell>
                <TableCell>{b.fileName}</TableCell>
                <TableCell>{new Date(b.createdAt).toLocaleString('hu-HU')}</TableCell>
                <TableCell><Chip size="small" color={STATUS_COLORS[b.status] || 'default'} label={b.status} /></TableCell>
                <TableCell>{b.created} / {b.skipped} / {b.failed}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </ResponsiveTableContainer>

      {/* 3. Kiválasztott import szolgáltatói + meghívás */}
      {selectedBatch && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }} justifyContent="space-between" mb={1}>
            <Typography variant="subtitle1" fontWeight={600}>
              #{selectedBatch.id} — {selectedBatch.fileName}
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              {problemRows.length > 0 && (
                <Button size="small" startIcon={<Download />} onClick={() => downloadCsv(problemRows, `import_${selectedBatch.id}_kihagyott.csv`)}>
                  Kihagyott/hibás CSV ({problemRows.length})
                </Button>
              )}
            </Stack>
          </Stack>

          {selectedBatch.status === 'RUNNING' && (
            <Box mb={2}>
              <Typography variant="body2" mb={0.5}>
                Folyamatban… {selectedBatch.created + selectedBatch.failed} / {selectedBatch.total - selectedBatch.skipped}
              </Typography>
              <LinearProgress
                variant="determinate"
                value={selectedBatch.total - selectedBatch.skipped > 0
                  ? ((selectedBatch.created + selectedBatch.failed) / (selectedBatch.total - selectedBatch.skipped)) * 100
                  : 0}
              />
            </Box>
          )}
          {selectedBatch.status === 'DONE' && imageWarnings > 0 && (
            <Alert severity="info" sx={{ mb: 2 }}>{imageWarnings} profilnál nem sikerült a képet letölteni — ezek kép nélkül jöttek létre.</Alert>
          )}

          <Divider sx={{ mb: 2 }} />

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }} mb={2}>
            <ToggleButtonGroup size="small" exclusive value={invitedFilter} onChange={(e, v) => v && setInvitedFilter(v)}>
              <ToggleButton value="no">Még nem meghívott</ToggleButton>
              <ToggleButton value="yes">Meghívott</ToggleButton>
              <ToggleButton value="all">Mind</ToggleButton>
            </ToggleButtonGroup>
            <TextField
              select
              size="small"
              label="Meghívó sablon"
              value={templateId}
              onChange={(e) => setTemplateId(e.target.value)}
              sx={{ minWidth: 260 }}
            >
              <MenuItem value="">Automatikus (aktív sablonok közül)</MenuItem>
              {inviteTemplates.map(t => (
                <MenuItem key={t.id} value={t.id}>{t.name}{t.isActive ? '' : ' (inaktív)'}</MenuItem>
              ))}
            </TextField>
            <Button
              variant="outlined"
              startIcon={<Visibility />}
              disabled={!templateId || !firstSelectedProvider || previewBusy}
              onClick={() => openInvitePreview(firstSelectedProvider)}
            >
              Előnézet
            </Button>
            <Button
              variant="contained"
              startIcon={inviting ? <CircularProgress size={16} color="inherit" /> : <Send />}
              disabled={selectedIds.length === 0 || inviting}
              onClick={() => sendInvites(selectedIds)}
              sx={darkButtonSx}
            >
              Kijelöltek meghívása ({selectedIds.length})
            </Button>
            {inviteProgress && (
              <Typography variant="body2" color="text.secondary">
                Küldés… {inviteProgress.done} / {inviteProgress.total}
              </Typography>
            )}
            {inviteQuota && (
              <Chip
                size="small"
                variant="outlined"
                color={inviteQuota.sentToday >= inviteQuota.limit ? 'error' : inviteQuota.sentToday >= inviteQuota.limit * 0.8 ? 'warning' : 'default'}
                label={`Ma kiment: ${inviteQuota.sentToday} / ${inviteQuota.limit} meghívó`}
              />
            )}
          </Stack>

          <ResponsiveTableContainer stickyLastColumn component="div" sx={{ maxHeight: 600 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox checked={allSelected} indeterminate={selectedIds.length > 0 && !allSelected} onChange={toggleAll} />
                  </TableCell>
                  <TableCell>Név</TableCell>
                  <TableCell>Kategória</TableCell>
                  <TableCell>Város</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Kép</TableCell>
                  <TableCell>Állapot</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {batchProviders.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 3, color: 'text.secondary' }}>Nincs találat.</TableCell>
                  </TableRow>
                ) : batchProviders.map(p => (
                  <TableRow key={p.id} hover>
                    <TableCell padding="checkbox">
                      <Checkbox disabled={p.status !== 'DRAFT'} checked={selectedIds.includes(p.id)} onChange={() => toggleOne(p.id)} />
                    </TableCell>
                    <TableCell>{p.businessName}</TableCell>
                    <TableCell>{p.category?.name || '—'}</TableCell>
                    <TableCell>{p.city || '—'}</TableCell>
                    <TableCell>{p.user?.email}</TableCell>
                    <TableCell>
                      {p.user?.avatarUrl
                        ? <Box component="img" src={p.user.avatarUrl} alt="" sx={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 1 }} />
                        : '—'}
                    </TableCell>
                    <TableCell>
                      {p.status !== 'DRAFT' ? (
                        <Chip size="small" color={p.status === 'APPROVED' ? 'success' : 'default'} label={p.status === 'APPROVED' ? 'Élesítve' : p.status} />
                      ) : p.invitedAt ? (
                        <Chip size="small" color="info" label={`Meghívva: ${new Date(p.invitedAt).toLocaleDateString('hu-HU')}`} />
                      ) : (
                        <Chip size="small" variant="outlined" label="Nincs meghívva" />
                      )}
                    </TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      <Button size="small" startIcon={<Edit fontSize="small" />} onClick={() => navigate(`/admin/providers/edit/${p.id}`)}>
                        Szerkesztés
                      </Button>
                      {p.status === 'DRAFT' && (
                        <Tooltip title={templateId ? 'Meghívó előnézete ezzel a szolgáltatóval' : 'Válassz sablont az előnézethez'}>
                          <span>
                            <IconButton size="small" disabled={!templateId || previewBusy} onClick={() => openInvitePreview(p)}>
                              <Visibility fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                      {p.status === 'DRAFT' && (
                        <Button size="small" startIcon={<Send fontSize="small" />} disabled={inviting} onClick={() => sendInvites([p.id])}>
                          {p.invitedAt ? 'Újraküldés' : 'Meghívás'}
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </ResponsiveTableContainer>
        </Paper>
      )}
      <Dialog open={!!invitePreview} onClose={() => setInvitePreview(null)} maxWidth="md" fullWidth>
        <DialogHeader icon={<Visibility />} title={`Előnézet: ${invitePreview?.businessName || ''}`} onClose={() => setInvitePreview(null)} />
        <DialogContent>
          {invitePreview && (
            <>
              <Typography variant="body2">Címzett: <strong>{invitePreview.to}</strong></Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>Tárgy: <strong>{invitePreview.subject}</strong></Typography>
              <Box
                component="iframe"
                title="Meghívó előnézet"
                srcDoc={invitePreview.html}
                sandbox=""
                sx={{ width: '100%', height: '60vh', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}
              />
              <Typography variant="caption" color="text.secondary">A gomb és a „nem kérem” link az előnézetben mintalink, élesben a szolgáltató saját egyedi linkjei kerülnek bele.</Typography>
            </>
          )}
        </DialogContent>
        <DialogActions><Button onClick={() => setInvitePreview(null)}>Bezár</Button></DialogActions>
      </Dialog>
      {confirmDialog}
    </Box>
  );
}
